import React, { useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from '../api/client';
import { useAuth } from '../contexts/AuthContext';
import { API } from '../api/config';
import LanguageSwitcher from '../components/LanguageSwitcher';
import { CardNumberElement, CardExpiryElement, CardCvcElement, Elements, useStripe, useElements } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import { stripeElementStyle } from '../stripeElementStyle';
import { calculateEstimatedReward } from '../utils/reward';
import { calculateStage1Quote, Stage1PaymentMethod } from '../utils/stage1';
import { AlertCircle, ShieldCheck, Loader2 } from 'lucide-react';

const stripePk = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || 'pk_test_TYooMQauvdEDq54NiTphI7jx';
const stripePromise = loadStripe(stripePk);

const QuickQuote: React.FC = () => {
  const { t, i18n } = useTranslation();
  const { register } = useAuth();

  const [projectUrl, setProjectUrl] = useState('');
  const [itemName, setItemName] = useState('');
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [quantity, setQuantity] = useState(1);
  const [unitPrice, setUnitPrice] = useState<number | ''>('');
  const [currency] = useState<'GBP'>('GBP');
  const [shippingCountry, setShippingCountry] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [color, setColor] = useState('');
  const [size, setSize] = useState('');
  const [discountCode, setDiscountCode] = useState('');
  const [otherInfo, setOtherInfo] = useState('');

  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  const [step, setStep] = useState<'product' | 'details' | 'register' | 'preview' | 'confirmed'>('product');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Payment method (BDD 1-A line 16-17: card / bank transfer / reward balance
  // — todo.txt 13.3 added this to OrderPortal.tsx but missed this page,
  // leaving /quote card-only; closing that parity gap here)
  const [paymentMethod, setPaymentMethod] = useState<Stage1PaymentMethod>('card');
  const [rewardBalance, setRewardBalance] = useState<number | null>(null);

  React.useEffect(() => {
    // Runs on mount AND whenever `step` changes — critical for the 'preview'
    // transition specifically, since the customer only becomes authenticated
    // mid-flow (at the 'register' step) and a mount-only effect would run
    // before any token exists, permanently leaving the balance unfetched.
    const token = localStorage.getItem('access_token');
    if (!token) return;
    axios.get(API.getRewardBalance(), { headers: { Authorization: `Bearer ${token}` } })
      .then((res) => setRewardBalance(res.data.balance))
      .catch(() => {});
  }, [step]);

  const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImageFile(file);
    const reader = new FileReader();
    reader.onload = () => setImagePreview(reader.result as string);
    reader.readAsDataURL(file);
  };

  const isUkShippingAddress = (country: string) => {
    const normalized = country.trim().toLowerCase();
    return ['england', 'scotland', 'wales', 'northern ireland', 'isle of man', 'uk', 'united kingdom', 'gb', 'great britain'].includes(normalized);
  };

  const showUkWarning = isUkShippingAddress(shippingCountry);

  const handleNextToDetails = () => {
    if (!projectUrl.trim()) { setError(t('quote.errors.urlRequired')); return; }
    if (!itemName.trim()) { setError(t('quote.errors.nameRequired')); return; }
    if (unitPrice === '' || Number(unitPrice) <= 0) { setError(t('quote.errors.priceRequired')); return; }
    setError('');
    setStep('details');
  };

  const handleNextToRegister = () => {
    setError('');
    setStep('register');
  };

  const handleCreateAccount = async () => {
    setError('');
    if (!fullName.trim() || !email.trim() || !password || !confirmPassword) {
      setError('Please provide your full name, email, and password.');
      return;
    }

    if (password.length < 8) {
      setError('Password must be at least 8 characters long.');
      return;
    }

    if (password !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }

    setLoading(true);
    try {
      await register(email.trim(), password, fullName.trim());
      setStep('preview');
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Registration failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleAuthorize = async () => {
    setLoading(true);
    setError('');
    try {
      const token = localStorage.getItem('access_token');
      if (!token) {
        setError('Your account session is not available. Please sign in and try again.');
        setLoading(false);
        return;
      }
      const headers = { Authorization: `Bearer ${token}` };

      // BDD 1-A line 7 (todo.txt 20.3): quantity/color/size/discount code
      // were already collected by this wizard and shown back to the
      // customer on the preview step, but never actually reached the
      // backend — quantity only ever fed the local total, and discount
      // code/image were silently dropped. `category` keeps the same
      // mashed description it always has (Orders.tsx/admin views already
      // read it as a display line); quantity/size/color/discount_code are
      // now sent as their own real fields too.
      const description = [itemName, color, size].filter(Boolean).join(' / ');
      const stage1 = calculateStage1Quote(
        [{ price: Number(unitPrice), quantity }],
        paymentMethod,
      );
      const res = await axios.post(
        API.createOrder(),
        {
          total_amount: stage1.paymentTotal,
          currency: 'gbp',
          payment_method: paymentMethod,
          items: [
            {
              retailer: itemName.trim(),
              url: projectUrl.trim(),
              price: Number(unitPrice),
              category: description || 'Quote request',
              quantity,
              size: size.trim() || undefined,
              color: color.trim() || undefined,
              discount_code: discountCode.trim() || undefined,
            },
          ],
        },
        { headers },
      );

      const { client_secret, order_id } = res.data;
      setOrderId(order_id);

      // Stage-1 Wise / bank-transfer reference derives deterministically from
      // the order id ('INC' + first 8 chars, uppercased) — same rule the
      // backend stores. Compute it directly so the Wise panel never depends on
      // a second GET round-trip.
      const ref = 'INC' + order_id.replace(/-/g, '').toUpperCase().slice(0, 8);
      setOrderReference(ref);

      if (imageFile) {
        try {
          const orderRes = await axios.get(API.getOrder(order_id), { headers });
          const itemId = orderRes.data.items?.[0]?.id;
          if (itemId) {
            const formData = new FormData();
            formData.append('file', imageFile);
            await axios.post(API.uploadOrderItemImage(order_id, itemId), formData, {
              headers: { ...headers, 'Content-Type': 'multipart/form-data' },
            });
          }
        } catch {
          // A failed image attach shouldn't block the purchase itself.
        }
      }

      // Persist the destination the customer already entered on step 1
      // (BDD 1-A line 41-42) so the real order can actually apply the
      // UK-domestic £0-reward rule, rather than that country only ever
      // driving the pre-purchase estimate shown here.
      if (shippingCountry.trim()) {
        await axios.post(
          API.setOrderShippingAddress(order_id),
          { country: shippingCountry.trim(), address: { country: shippingCountry.trim() } },
          { headers },
        ).catch(() => {});
      }

      if (paymentMethod === 'reward') {
        await axios.post(API.confirmPreauth(order_id), {}, { headers });
        setLoading(false);
        onPaymentSuccess();
        return;
      }

      if (paymentMethod === 'bank_transfer') {
        // Wise bank transfer: order is created in pending_preauth, funds are
        // confirmed manually by admin once the transfer arrives (Stage-1
        // two-stage billing). Show the Wise instructions + reference.
        setShowWiseInfo(true);
        setLoading(false);
        return;
      }

      // Stripe confirmation is handled by the card form component
      // Once confirmed, it calls onPaymentSuccess which sets step to confirmed
      setClientSecret(client_secret);
      setShowCardForm(true);
      setLoading(false);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Submission failed. Please try again.');
      setLoading(false);
    }
  };

  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const [showCardForm, setShowCardForm] = useState(false);
  const [orderId, setOrderId] = useState<string | null>(null);
  const [orderReference, setOrderReference] = useState<string>('');
  const [showWiseInfo, setShowWiseInfo] = useState(false);

  const onPaymentSuccess = () => {
    setStep('confirmed');
  };

  const StripeCardForm: React.FC<{ secret: string; onSuccess: () => void; orderId: string }> = ({ secret, onSuccess, orderId }) => {
    const stripe = useStripe();
    const elements = useElements();
    const [processing, setProcessing] = useState(false);
    const [cardError, setCardError] = useState<string | null>(null);
    const [cardComplete, setCardComplete] = useState(false);

    const handleCardPay = async (e: React.FormEvent) => {
      e.preventDefault();
      if (!stripe || !elements) return;
      setProcessing(true);
      setCardError(null);

      try {
        const cardNumber = elements.getElement(CardNumberElement);
        if (!cardNumber) {
          setCardError('Card element not found. Please refresh.');
          setProcessing(false);
          return;
        }

        const { error: confirmError, paymentIntent } = await stripe.confirmCardPayment(secret, {
          payment_method: { card: cardNumber },
        });

        if (confirmError) {
          setCardError(confirmError.message || 'Payment failed.');
          setProcessing(false);
          return;
        }

        if (paymentIntent?.status === 'requires_capture' || paymentIntent?.status === 'succeeded') {
          const token = localStorage.getItem('access_token');
          await axios.post(API.confirmPreauth(orderId), {}, { headers: { Authorization: `Bearer ${token}` } });
          onSuccess();
        } else {
          setCardError('Unexpected payment status: ' + paymentIntent?.status);
          setProcessing(false);
        }
      } catch (err: any) {
        setCardError(err.response?.data?.detail || err.message || 'Payment error.');
        setProcessing(false);
      }
    };

    return (
      <form onSubmit={handleCardPay} style={{ marginTop: '20px' }}>
        <div style={{ background: 'var(--text-charcoal)', color: '#fff', padding: 'clamp(20px, 5vw, 32px)', borderRadius: '8px', marginBottom: '16px' }}>
          <p style={{ fontSize: '13px', opacity: 0.7, marginBottom: '16px' }}>Card Details</p>

          <label style={{ color: 'rgba(255,255,255,0.7)', fontSize: '12px', marginBottom: '8px', display: 'block' }}>Card Number</label>
          <div style={{ padding: '12px', background: '#fff', borderRadius: '6px', marginBottom: '12px', minHeight: '44px', display: 'flex', alignItems: 'center', width: '100%', boxSizing: 'border-box' }}>
            <CardNumberElement
              options={{ style: stripeElementStyle }}
              onChange={(e) => { setCardComplete(e.complete); if (e.error) setCardError(e.error.message); else setCardError(null); }}
            />
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
            <div>
              <label style={{ color: 'rgba(255,255,255,0.7)', fontSize: '12px', marginBottom: '8px', display: 'block' }}>Expiry Date</label>
              <div style={{ padding: '12px', background: '#fff', borderRadius: '6px', minHeight: '44px', display: 'flex', alignItems: 'center' }}>
                <CardExpiryElement options={{ style: stripeElementStyle }} />
              </div>
            </div>
            <div>
              <label style={{ color: 'rgba(255,255,255,0.7)', fontSize: '12px', marginBottom: '8px', display: 'block' }}>CVC</label>
              <div style={{ padding: '12px', background: '#fff', borderRadius: '6px', minHeight: '44px', display: 'flex', alignItems: 'center' }}>
                <CardCvcElement options={{ style: stripeElementStyle }} />
              </div>
            </div>
          </div>
        </div>

        {cardError && (
          <div style={{ color: '#FCA5A5', fontSize: '14px', marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '8px', justifyContent: 'center', background: 'rgba(239,68,68,0.1)', padding: '12px', borderRadius: '6px' }}>
            <AlertCircle size={14} /> {cardError}
          </div>
        )}

        <button type="submit" className="btn btn-primary" style={{ width: '100%', height: '50px', fontSize: '16px', justifyContent: 'center' }} disabled={!stripe || !cardComplete || processing}>
          {processing ? <><Loader2 className="animate-spin" size={18} /> Processing...</> : <><ShieldCheck size={18} /> Confirm & Pay</>}
        </button>

        <div style={{ marginTop: '16px', fontSize: '12px', color: '#64748B', textAlign: 'center' }}>
          🔒 Secured by Stripe
        </div>
      </form>
    );
  };

  const totalPrice = unitPrice === '' ? 0 : Number(unitPrice) * quantity;
  // Stage-1 two-stage billing (兩段式計費) procurement quote — mirrors the
  // backend engine (OrderService._compute_stage1). Shows net − 8% reward
  // rebate + buyer fee (+3% card processing) = the amount the customer pays.
  const stage1 = calculateStage1Quote(
    [{ price: unitPrice === '' ? 0 : Number(unitPrice), quantity }],
    paymentMethod,
  );
  // BDD 1-A line 8-9/18/31 formula term is "10 x 件數" (items_count) — the
  // backend (rewards/service.py::calculate_estimated_reward, invoked at the
  // real crediting point in orders/service.py::verify_invoice) counts
  // OrderItem *rows*, not summed quantity. handleAuthorize() below always
  // creates exactly one row per QuickQuote order regardless of quantity, so
  // this must stay `10 * 1` to match what will actually be credited —
  // multiplying by `quantity` overstated the promised reward on this exact
  // page (found during a fifth frontend-coverage audit).
  const estimatedReward = showUkWarning ? 0 : calculateEstimatedReward(totalPrice);

  const formatPrice = (amount: number) => {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'GBP',
      minimumFractionDigits: 2,
    }).format(amount);
  };

  return (
    <main className="quote-page">
      <nav className="quote-nav">
        <Link to="/" className="quote-brand">INCORD UK</Link>
        <div className="quote-nav-links">
          <a href="https://incord.uk/#services">{t('common.services')}</a>
          <a href="https://incord.uk/#journey">{t('home.journey.title')}</a>
          <Link to="/login" className="quote-nav-cta">{t('common.signUp')} / {t('common.login')}</Link>
          <LanguageSwitcher />
        </div>
      </nav>

      <div className="quote-container">
        <div className="quote-steps">
          <div className={`quote-step ${step === 'product' ? 'active' : ''} ${step !== 'product' ? 'done' : ''}`}>
            <span className="step-num">1</span>
            <span className="step-label">{t('quote.steps.product')}</span>
          </div>
          <div className="step-connector" />
          <div className={`quote-step ${step === 'details' ? 'active' : ''} ${step === 'register' || step === 'preview' || step === 'confirmed' ? 'done' : ''}`}>
            <span className="step-num">2</span>
            <span className="step-label">{t('quote.steps.details')}</span>
          </div>
          <div className="step-connector" />
          <div className={`quote-step ${step === 'register' ? 'active' : ''} ${step === 'preview' || step === 'confirmed' ? 'done' : ''}`}>
            <span className="step-num">3</span>
            <span className="step-label">{t('quote.steps.register')}</span>
          </div>
          <div className="step-connector" />
          <div className={`quote-step ${step === 'preview' ? 'active' : ''} ${step === 'confirmed' ? 'done' : ''}`}>
            <span className="step-num">4</span>
            <span className="step-label">{t('quote.steps.preview')}</span>
          </div>
        </div>

        {error && <div className="quote-error">{error}</div>}

        {step === 'product' && (
          <div className="quote-card">
            <div style={{
              background: 'rgba(74, 93, 35, 0.06)',
              border: '1px solid rgba(74, 93, 35, 0.15)',
              borderRadius: '6px',
              padding: '14px 18px',
              marginBottom: '24px',
              fontSize: '13px',
              lineHeight: 1.5,
              color: '#4A5D23',
            }}>
              {i18n.language.startsWith('zh') ? (
                <>
                  <strong>🔑 已有帳號？</strong>{' '}
                  <a href="/order" style={{ color: '#4A5D23', textDecoration: 'underline' }}>登入後直接在 Smart Order 送出採購需求</a>
                </>
              ) : (
                <>
                  <strong>🔑 Already a member?</strong>{' '}
                  <a href="/order" style={{ color: '#4A5D23', textDecoration: 'underline' }}>Sign in and submit requests directly via Smart Order</a>
                </>
              )}
            </div>
            <h2>{t('quote.product.title')}</h2>
            <p className="quote-subtitle">{t('quote.product.subtitle')}</p>

            <h3 className="quote-section-title">{t('quote.product.sectionTitle')}</h3>

            <div className="quote-form">
              <div className="quote-field">
                <label>{t('quote.product.projectUrl')} <span className="required">*</span></label>
                <input
                  type="url"
                  value={projectUrl}
                  onChange={e => setProjectUrl(e.target.value)}
                  placeholder="e.g., Paste link of the product page from Harrods, Net-a-Porter, source e-commerce or Official Brand Website"
                  className="quote-input"
                />
              </div>

              <div className="quote-field">
                <label>{t('quote.product.itemName')} <span className="required">*</span></label>
                <input
                  type="text"
                  value={itemName}
                  onChange={e => setItemName(e.target.value)}
                  placeholder="e.g., Loro Piana Sea-Sail Walk Loafer"
                  className="quote-input"
                />
              </div>

              <div className="quote-price-row">
                <div className="quote-field" style={{ flex: 1 }}>
                  <label>{t('quote.product.quantity')}</label>
                  <div className="quote-qty-control">
                    <button
                      type="button"
                      className="qty-btn"
                      onClick={() => setQuantity(Math.max(1, quantity - 1))}
                    >−</button>
                    <span className="qty-value">{quantity}</span>
                    <button
                      type="button"
                      className="qty-btn"
                      onClick={() => setQuantity(quantity + 1)}
                    >+</button>
                  </div>
                </div>
                <div className="quote-field" style={{ flex: 2 }}>
                  <label>{t('quote.product.unitPrice')} <span className="required">*</span></label>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={unitPrice}
                    onChange={e => setUnitPrice(e.target.value === '' ? '' : Number(e.target.value))}
                    placeholder="0.00"
                    className="quote-input"
                  />
                </div>
                <div className="quote-field" style={{ width: '130px' }}>
                  <label>{t('quote.product.currency')}</label>
                  <select value={currency} disabled className="quote-input">
                    <option value="GBP">GBP £</option>
                  </select>
                </div>
              </div>

              <div className="quote-field">
                <label htmlFor="shipping-destination">Shipping destination</label>
                <select
                  id="shipping-destination"
                  value={shippingCountry}
                  onChange={(e) => setShippingCountry(e.target.value)}
                  className="quote-input"
                >
                  <option value="">Select destination</option>
                  <option value="United Kingdom">In UK</option>
                  <option value="Outside UK">Outside UK</option>
                </select>
              </div>

              {/* Choice-driven estimate: nothing is promised until the customer
                  picks a destination; In UK → £0 (red notice below), Outside
                  UK → bonus estimate. Values match the backend's
                  rewards/service.py::UK_DOMESTIC_LOCATIONS check so the same
                  rule applies at credit time. */}
              {unitPrice !== '' && Number(unitPrice) > 0 && shippingCountry !== '' && !showUkWarning && (
                <div className="quote-reward">
                  <span className="reward-label">Estimated Reward</span>
                  <span className="reward-value">{formatPrice(calculateEstimatedReward(Number(unitPrice) * quantity))}</span>
                </div>
              )}

              {showUkWarning && (
                <div className="quote-reward" style={{ background: 'rgba(239, 68, 68, 0.08)', borderColor: 'rgba(239, 68, 68, 0.2)' }}>
                  <span className="reward-label">UK domestic shipping</span>
                  <span className="reward-value" style={{ color: '#b91c1c' }}>£0 reward</span>
                </div>
              )}

              <button className="btn btn-primary quote-continue" onClick={handleNextToDetails}>
                {t('quote.product.continue')}
              </button>
            </div>
          </div>
        )}

        {step === 'details' && (
          <div className="quote-card">
            <h2>{t('quote.details.title')}</h2>

            <div className="quote-form">
              <div className="quote-field">
                <label>{t('quote.details.color')} <small>({t('quote.details.optional')})</small></label>
                <input
                  type="text"
                  value={color}
                  onChange={e => setColor(e.target.value)}
                  placeholder="e.g. Black, Ivory"
                  className="quote-input"
                />
              </div>

              <div className="quote-field">
                <label>{t('quote.details.size')} <small>({t('quote.details.optional')})</small></label>
                <input
                  type="text"
                  value={size}
                  onChange={e => setSize(e.target.value)}
                  placeholder="e.g. Medium, One Size, 38"
                  className="quote-input"
                />
              </div>

              <div className="quote-field">
                <label>{t('quote.details.discountCode')} <small>({t('quote.details.optional')})</small></label>
                <input
                  type="text"
                  value={discountCode}
                  onChange={e => setDiscountCode(e.target.value)}
                  placeholder="e.g. SALE20"
                  className="quote-input"
                />
              </div>

              <div className="quote-field">
                <label>{t('quote.details.otherInfo')} <small>({t('quote.details.optional')})</small></label>
                <textarea
                  value={otherInfo}
                  onChange={e => setOtherInfo(e.target.value)}
                  placeholder="Any other details..."
                  className="quote-input quote-textarea"
                  rows={3}
                />
              </div>

              <div className="quote-field">
                <label>{t('quote.product.imageUpload')}</label>
                <div className="quote-upload-area" onClick={() => fileInputRef.current?.click()}>
                  <input
                    type="file"
                    accept="image/*"
                    ref={fileInputRef}
                    style={{ display: 'none' }}
                    onChange={handleImageUpload}
                  />
                  {imagePreview ? (
                    <img src={imagePreview} alt="Preview" className="quote-upload-preview" />
                  ) : (
                    <div className="quote-upload-placeholder">
                      <span className="upload-icon">📷</span>
                      <span>Click to upload image</span>
                    </div>
                  )}
                </div>
              </div>

              <button className="btn btn-primary quote-continue" onClick={handleNextToRegister}>
                {t('quote.details.continue')}
              </button>
            </div>
          </div>
        )}

        {step === 'register' && (
          <div className="quote-card">
            <h2>{t('quote.register.title')}</h2>
            <p className="quote-subtitle">{t('quote.register.subtitle')}</p>

            <div className="quote-form">
              <div className="quote-field">
                <label>Full Name <span className="required">*</span></label>
                <input
                  type="text"
                  value={fullName}
                  onChange={e => setFullName(e.target.value)}
                  placeholder="Your full name"
                  className="quote-input"
                />
              </div>
              <div className="quote-field">
                <label>Email <span className="required">*</span></label>
                <input
                  type="email"
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  className="quote-input"
                />
              </div>
              <div className="quote-field">
                <label>Phone <small>(optional)</small></label>
                <input
                  type="tel"
                  value={phone}
                  onChange={e => setPhone(e.target.value)}
                  placeholder="+44 7XXX XXXXXX"
                  className="quote-input"
                />
              </div>
              <div className="quote-field">
                <label>Password <span className="required">*</span></label>
                <input
                  type="password"
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  placeholder="Create a password"
                  className="quote-input"
                />
              </div>
              <div className="quote-field">
                <label>Confirm Password <span className="required">*</span></label>
                <input
                  type="password"
                  value={confirmPassword}
                  onChange={e => setConfirmPassword(e.target.value)}
                  placeholder="Re-enter your password"
                  className="quote-input"
                />
              </div>
            </div>

            <div className="quote-register-skip">
              <button className="btn btn-primary quote-continue" onClick={handleCreateAccount} disabled={loading}>
                {loading ? 'Creating account...' : 'Create account & continue'}
              </button>
            </div>
          </div>
        )}

        {step === 'preview' && (
          <div className="quote-card">
            <h2>{t('quote.preview.title')}</h2>

            <div className="quote-preview-details">
              <div className="preview-row">
                <span className="preview-label">URL</span>
                <span className="preview-value">{projectUrl}</span>
              </div>
              <div className="preview-row">
                <span className="preview-label">{t('quote.product.itemName')}</span>
                <span className="preview-value">{itemName}</span>
              </div>
              <div className="preview-row">
                <span className="preview-label">{t('quote.product.quantity')}</span>
                <span className="preview-value">{quantity}</span>
              </div>
              <div className="preview-row">
                <span className="preview-label">{t('quote.product.unitPrice')}</span>
                <span className="preview-value">{formatPrice(Number(unitPrice))}</span>
              </div>
              {color && <div className="preview-row"><span className="preview-label">{t('quote.details.color')}</span><span className="preview-value">{color}</span></div>}
              {size && <div className="preview-row"><span className="preview-label">{t('quote.details.size')}</span><span className="preview-value">{size}</span></div>}
              {discountCode && <div className="preview-row"><span className="preview-label">{t('quote.details.discountCode')}</span><span className="preview-value">{discountCode}</span></div>}
              <div className="preview-divider" />
              {/* Stage-1 procurement quote breakdown (兩段式計費) */}
              <div className="preview-row">
                <span className="preview-label">Item subtotal (net)</span>
                <span className="preview-value">{formatPrice(stage1.netItemsTotal)}</span>
              </div>
              <div className="preview-row">
                <span className="preview-label">Reward rebate (8%)</span>
                <span className="preview-value" style={{ color: '#4A5D4E' }}>−{formatPrice(stage1.rewardRebate)}</span>
              </div>
              <div className="preview-row">
                <span className="preview-label">Buyer / service fee</span>
                <span className="preview-value">+{formatPrice(stage1.buyerFee)}</span>
              </div>
              {paymentMethod === 'card' && (
                <div className="preview-row">
                  <span className="preview-label">Card processing (3%)</span>
                  <span className="preview-value">+{formatPrice(stage1.processingFee)}</span>
                </div>
              )}
              <div className="preview-divider" />
              <div className="preview-row preview-total">
                <span className="preview-label">Procurement total (Stage 1)</span>
                <span className="preview-value">{formatPrice(stage1.paymentTotal)}</span>
              </div>
            </div>

            {/* Same choice-driven rule as step 1: Outside UK → estimate,
                In UK → the red £0 notice below is the single source of truth. */}
            {!showUkWarning && (
              <div className="quote-reward" style={{ marginTop: '16px' }}>
                <span className="reward-label">Estimated Reward</span>
                <span className="reward-value">{formatPrice(estimatedReward)}</span>
              </div>
            )}

            {showUkWarning && (
              <div className="quote-reward" style={{ background: 'rgba(239, 68, 68, 0.08)', borderColor: 'rgba(239, 68, 68, 0.2)' }}>
                <span className="reward-label">UK domestic shipping</span>
                <span className="reward-value" style={{ color: '#b91c1c' }}>£0 reward</span>
              </div>
            )}

            {!showCardForm ? (
              <>
                {/* Payment method selector (BDD 1-A line 16-17) — three:
                    card, Wise (bank transfer), reward balance */}
                <div style={{ display: 'flex', gap: '10px', marginTop: '24px', flexWrap: 'wrap' }}>
                  {([
                    { value: 'card' as const, label: 'Credit / Debit Card' },
                    { value: 'bank_transfer' as const, label: 'Wise (Bank Transfer)' },
                    { value: 'reward' as const, label: 'Reward Balance' },
                  ]).map((opt) => (
                    <button
                      key={opt.value}
                      type="button"
                      className={paymentMethod === opt.value ? 'btn btn-primary' : 'btn btn-outline'}
                      style={{ flex: '1 1 150px', fontSize: '13px' }}
                      onClick={() => setPaymentMethod(opt.value)}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>

                {paymentMethod === 'reward' && (
                  <div style={{ marginTop: '12px', fontSize: '13px', color: '#64748B' }}>
                    Reward balance: £{(rewardBalance ?? 0).toFixed(2)}
                    {rewardBalance !== null && rewardBalance < stage1.paymentTotal && (
                      <span style={{ color: '#b91c1c', display: 'block', marginTop: '4px' }}>
                        Your reward balance doesn't cover this order.
                      </span>
                    )}
                  </div>
                )}
                {showWiseInfo && (
                  <div style={{ marginTop: '16px', padding: '16px', border: '1px solid var(--border-light)', borderRadius: '8px', background: 'var(--bg-oatmeal)' }}>
                    <h4 style={{ marginBottom: '8px' }}>Pay via Wise — Order Reference</h4>
                    <p style={{ fontSize: '13px', lineHeight: 1.7, marginBottom: '6px' }}>
                      Please transfer <strong>{formatPrice(stage1.paymentTotal)}</strong> to our UK account via Wise and
                      include the reference below so we can match your payment.
                    </p>
                    <div style={{ fontSize: '15px', fontWeight: 700, letterSpacing: '1px', color: 'var(--accent-moss)' }}>
                      {orderReference}
                    </div>
                    <p style={{ fontSize: '12px', color: '#64748B', marginTop: '8px' }}>
                      Once your transfer arrives, our team will confirm it and begin sourcing. View the order anytime in My Orders.
                    </p>
                    <Link to="/orders" className="btn btn-primary" style={{ marginTop: '14px' }}>
                      View My Orders
                    </Link>
                  </div>
                )}

                {!showWiseInfo && (
                  <button
                    className="btn btn-primary quote-continue"
                    onClick={handleAuthorize}
                    disabled={loading || (paymentMethod === 'reward' && rewardBalance !== null && rewardBalance < stage1.paymentTotal)}
                    style={{ width: '100%', justifyContent: 'center', marginTop: '16px' }}
                  >
                    {loading
                      ? t('quote.preview.processing')
                      : paymentMethod === 'card'
                        ? t('quote.preview.authorize')
                        : paymentMethod === 'bank_transfer'
                          ? 'Continue with Wise'
                          : 'Pay with Reward Balance'}
                  </button>
                )}
              </>
            ) : (
              clientSecret && (
                <Elements stripe={stripePromise} options={{ clientSecret }}>
                  <StripeCardForm secret={clientSecret} onSuccess={onPaymentSuccess} orderId={orderId!} />
                </Elements>
              )
            )}
          </div>
        )}

        {step === 'confirmed' && (
          <div className="quote-card quote-confirmed">
            <div className="confirmed-icon">✓</div>
            <h2>Request Submitted!</h2>
            <p>
              Your account has been created and your smart order is now in progress. We'll review it and confirm stock availability within 24 hours.
            </p>
            <Link to="/orders" className="btn btn-primary" style={{ marginTop: '30px' }}>
              View My Orders
            </Link>
          </div>
        )}
      </div>
    </main>
  );
};

export default QuickQuote;
